import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { api } from "../lib/api";
import { useLanguage } from "../context/LanguageContext";
import { Reveal } from "./Reveal";

const STATUS_STYLES = {
  "Concluídos": "border-[#3FB95066] bg-basalt/75 text-[#3FB950]",
  "Em Desenvolvimento": "border-[#58A6FF66] bg-basalt/75 text-[#58A6FF]",
  "Reabilitação": "border-[#D2992266] bg-basalt/75 text-[#D29922]",
  "Construção Nova": "border-[#F0883E66] bg-basalt/75 text-[#F0883E]",
  "Vendido em Projeto": "border-[#D4AF3766] bg-basalt/75 text-[#D4AF37]",
  "Em Construção": "border-[#4FC3D966] bg-basalt/75 text-[#4FC3D9]",
};
const STATUSES = Object.keys(STATUS_STYLES);

const slug = (s) => s.toLowerCase().replace(/\s+/g, "-");

export const Projects = () => {
  const { t } = useLanguage();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api
      .get("/projects")
      .then((r) => setProjects(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  const visible = useMemo(
    () => (filter === "all" ? projects : projects.filter((p) => p.status === filter)),
    [projects, filter]
  );

  return (
    <section id="projetos" className="py-28 sm:py-36" data-testid="projects-section">
      <div className="mx-auto max-w-7xl px-6">
        <div className="flex flex-wrap items-end justify-between gap-8">
          <div>
            <Reveal>
              <p className="mb-5 font-mono text-[11px] uppercase tracking-[0.35em] text-terracotta">
                {t.projects.eyebrow}
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-display text-2xl font-normal leading-snug tracking-tight text-sand sm:text-3xl lg:text-4xl" data-testid="projects-title">
                {t.projects.title}
                <span className="ml-3 align-middle font-mono text-sm tracking-widest text-mist">
                  ({projects.length})
                </span>
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-4 max-w-lg text-sm leading-relaxed text-mist sm:text-base">
                {t.projects.subtitle}
              </p>
            </Reveal>
          </div>

          <Reveal delay={0.2} className="flex flex-wrap gap-2" data-testid="projects-filters">
            <button
              data-testid="projects-filter-all"
              onClick={() => setFilter("all")}
              className={`rounded-full border px-4 py-1.5 font-mono text-[11px] tracking-widest transition-colors ${
                filter === "all"
                  ? "border-terracotta bg-terracotta text-basalt"
                  : "border-white/15 text-sand/60 hover:border-terracotta/60 hover:text-sand"
              }`}
            >
              {t.projects.all}
            </button>
            {STATUSES.map((s) => (
              <button
                key={s}
                data-testid={`projects-filter-${slug(s)}`}
                onClick={() => setFilter(s)}
                className={`rounded-full border px-4 py-1.5 font-mono text-[11px] tracking-widest transition-colors ${
                  filter === s
                    ? "border-terracotta bg-terracotta text-basalt"
                    : "border-white/15 text-sand/60 hover:border-terracotta/60 hover:text-sand"
                }`}
              >
                {t.status[s]}
              </button>
            ))}
          </Reveal>
        </div>

        {loading && (
          <p className="mt-20 text-center font-mono text-xs tracking-widest text-mist" data-testid="projects-loading">
            {t.projects.loading}
          </p>
        )}
        {error && !loading && (
          <p className="mt-20 text-center font-mono text-xs tracking-widest text-terracotta" data-testid="projects-error">
            {t.projects.error}
          </p>
        )}
        {!loading && !error && visible.length === 0 && (
          <p className="mt-20 text-center font-mono text-xs tracking-widest text-mist" data-testid="projects-empty">
            {t.projects.empty}
          </p>
        )}

        <motion.div layout className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="projects-grid">
          <AnimatePresence mode="popLayout">
            {visible.map((p, i) => (
              <motion.article
                layout
                key={p.id}
                initial={{ opacity: 0, y: 28 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.55, delay: Math.min(i * 0.05, 0.3), ease: [0.22, 1, 0.36, 1] }}
                className="group relative overflow-hidden rounded-lg border border-white/10 bg-surface transition-colors duration-500 hover:border-terracotta/50"
                data-testid={`project-card-${p.id}`}
              >
                <Link to={`/projetos/${p.id}`} className="block" data-testid={`project-link-${p.id}`}>
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={p.image}
                      alt={p.name}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-basalt via-basalt/35 to-basalt/30" />
                    <div className="absolute left-4 top-4 flex flex-col items-start gap-2">
                      <span
                        className={`rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-widest backdrop-blur-sm ${STATUS_STYLES[p.status] || "border-white/20 bg-white/10 text-sand"}`}
                        data-testid={`project-status-${p.id}`}
                      >
                        {t.status[p.status] || p.status}
                      </span>
                      {p.for_sale && (
                        <span
                          className="rounded-full border border-[#D4AF3766] bg-basalt/75 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-gold backdrop-blur-sm"
                          data-testid={`project-forsale-${p.id}`}
                        >
                          {t.featured.forSale}
                        </span>
                      )}
                    </div>
                    <span className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full border border-white/20 bg-basalt/60 text-sand opacity-0 backdrop-blur-sm transition-all duration-300 group-hover:opacity-100">
                      <ArrowUpRight size={14} />
                    </span>
                    <div className="absolute inset-x-0 bottom-0 p-5">
                      <h3 className="font-display text-xl text-sand sm:text-2xl" data-testid={`project-name-${p.id}`}>
                        {p.name}
                      </h3>
                      <p className="mt-2 font-mono text-[11px] uppercase tracking-widest text-sand/60" data-testid={`project-meta-${p.id}`}>
                        {p.location}
                        {p.year ? ` · ${p.year}` : ""}
                        {p.typology ? ` · ${p.typology}` : ""}
                      </p>
                    </div>
                  </div>
                </Link>
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};
